import { useEffect, useState } from "react";

/**
 * Subscribes to a media query and re-renders when it flips.
 * SSR-safe (returns false until mount, then matches the real window).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * Tailwind's `md` breakpoint is 768px. Matches the `max-md:` modifiers
 * elsewhere so JS-side responsive decisions agree with CSS.
 */
export function useIsMobile(): boolean {
  return useMediaQuery("(max-width: 767px)");
}

/**
 * Below Tailwind's `lg` breakpoint (1024 px). Drives the top-level
 * swap to MobileApp so phones and iPad-portrait get a vertical layout
 * while laptops and iPad-landscape keep the desktop UI verbatim.
 */
export function useIsMobileLayout(): boolean {
  return useMediaQuery("(max-width: 1023px)");
}
